import { format, parse } from 'date-fns';
import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellDelete } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors as PopupMenuColors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { reminderTimes } from 'shared/constants';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { InteractionType } from 'shared/types/data-type';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { isValidTime } from 'shared/utils/is-valid-time';
import { parseDateTime } from 'shared/utils/parse-date-time';
import { processStringToHoursAndMinutes } from 'shared/utils/process-string-to-hours-and-minutes';

import * as Styled from './task-meta-interaction-reminder-time.style';

export type TaskMetaInteractionReminderTimeProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel'
> & {
  value?: number | null;
  onChange: (value?: number) => void;
  highlight?: boolean;
  timeFormat: TimeFormatOptions;
};

export const TaskMetaInteractionReminderTime: React.FC<
  TaskMetaInteractionReminderTimeProps
> = ({ value, onChange, highlight, timeFormat, ...rest }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [inputValue, setInputValue] = useState<string>(
    value ? parseDateTime(value, timeFormat) : '',
  );
  const [inputError, setInputError] = useState(false);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const parsedValue = useMemo(
    () => (value ? parseDateTime(value, timeFormat) : undefined),
    [timeFormat, value],
  );

  const onClear = () => {
    onChange();
    closeMenu();
  };

  const submit = () => {
    if (!inputRef.current) {
      return;
    }

    const inputVal = inputRef.current.value;

    if (!inputVal || isValidTime(inputValue, timeFormat)) {
      const date = inputVal
        ? parse(inputVal, timeFormat, new Date())
        : undefined;

      onChange(
        date
          ? (date.getHours() * 60 + date.getMinutes()) * 60 * 1000
          : undefined,
      );
    }

    closeMenu();
  };

  const handleInputChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setInputError(false);
    if (!target.value.length) {
      setInputValue('');
      return;
    }

    // Process textual input to the format we expect
    const hoursAndMinutes = processStringToHoursAndMinutes(
      target.value,
      timeFormat,
    );

    // Update the input
    setInputValue(hoursAndMinutes);
  };

  const _placeHolder = useMemo(() => {
    if (!reminderTimes.length) {
      return timeFormat;
    }

    const dateObj = new Date();
    dateObj.setHours(0, 0, 0, reminderTimes[0]);
    return format(dateObj, timeFormat);
  }, [timeFormat]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [menuOpen]);

  useKeysDown(
    ['Enter'],
    (e) => {
      if (inputRef.current && document.activeElement === inputRef.current) {
        e.preventDefault();
        submit();
        return;
      }

      setInputError(true);
    },
    { otherKeysInCombinationAllowed: true },
  );

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        start={<Icon icon={Bell} />}
        color={highlight && parsedValue ? Colors.TextPrimary : Colors.Inherit}
        onClick={openMenu}
        label={parsedValue}
        tooltipLabel={t(
          parsedValue
            ? 'task.reminder.tooltip.edit.label'
            : 'task.reminder.tooltip.add.label',
        )}
        selected={menuOpen}
        {...rest}
      />

      {menuOpen && (
        <PopupMenu
          referenceElement={containerRef}
          interactionType={InteractionType.Task}
          onOutside={submit}
        >
          <PopupMenuList hasBottomBorder>
            <PopupMenuListItem>
              <PopupMenuInput
                ref={inputRef}
                iconColor={inputError ? PopupMenuColors.Error : undefined}
                startIcon={Bell}
                textColor={inputError ? PopupMenuColors.Error : undefined}
                value={inputValue}
                placeholder={_placeHolder}
                onChange={handleInputChange}
              />
            </PopupMenuListItem>
          </PopupMenuList>

          <PopupMenuList hasBottomBorder={!!value} isScrollable>
            {reminderTimes.map((option) => {
              const onItem = () => {
                onChange(option);
                closeMenu();
              };

              const dateObj = new Date();
              dateObj.setHours(0, 0, 0, option);

              return (
                <PopupMenuListItem key={option}>
                  <PopupMenuButton
                    start={<Icon icon={Bell} />}
                    onClick={onItem}
                    tabIndex={0}
                    type="button"
                  >
                    {format(dateObj, timeFormat)}
                  </PopupMenuButton>
                </PopupMenuListItem>
              );
            })}
          </PopupMenuList>

          <PopupMenuList>
            {!!value && (
              <PopupMenuListItem>
                <PopupMenuButton
                  start={<Icon icon={BellDelete} />}
                  startColor={PopupMenuColors.Error}
                  textColor={PopupMenuColors.Error}
                  onClick={onClear}
                  tabIndex={0}
                >
                  {t('task.reminder.options.no-reminder.label')}
                </PopupMenuButton>
              </PopupMenuListItem>
            )}
          </PopupMenuList>
        </PopupMenu>
      )}
    </Styled.Container>
  );
};
